import type { DeleteWorkspaceModalProps } from "@v2/types/modalTypes";
import type { WorkspaceIssueType } from "schemas/generated/workspace";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldTitle,
} from "@replo/design-system/components/field/Field";
import { Input } from "@replo/design-system/components/input/Input";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { toast } from "@replo/design-system/components/toast/Toast";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import { skipToken } from "@tanstack/react-query";
import { Loader } from "@v2/components/Loader";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { projectUtils } from "@v2/trpc/routes/project";
import { userUtils } from "@v2/trpc/routes/user";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import { routes } from "@v2/utils/router";
import { useForm } from "react-hook-form";
import { generatePath, useNavigate } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import z from "zod";

type FormValues = {
  workspaceName: string;
};

const getIssueNavigationInfo = (
  issueType: WorkspaceIssueType,
  workspaceId: string,
): { path: string; linkText: string } | null => {
  return exhaustiveSwitch({ type: issueType })({
    subscription: {
      path: generatePath(routes.workspace.billing, { workspaceId }),
      linkText: "Go to Billing",
    },
    projects: null,
    experiments: {
      path: generatePath(routes.workspace.experiments.list, { workspaceId }),
      linkText: "Go to AB Tests",
    },
  });
};

export const DeleteWorkspaceModal: React.FC<
  DeleteWorkspaceModalProps & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }
> = ({ workspaceId, open, onOpenChange }) => {
  const isMobile = useIsMobile();
  const logEvent = useLogAnalytics();
  const navigate = useNavigate();

  // Note (Noah, 2025-02-22): Technically this could be a useCurrentWorkspaceId, but
  // since this modal specifically takes a workspaceId and could be used in other
  // contexts, we're fetching workspace data directly from the id here.
  const { data } = workspaceRouter.getById.useQuery(
    workspaceId ? { id: workspaceId } : skipToken,
  );
  const { data: canDeleteData, isLoading: isCheckingCanDelete } =
    workspaceRouter.canDelete.useQuery(workspaceId ?? skipToken);
  const { mutateAsync: deleteWorkspace, isPending: isDeleting } =
    workspaceRouter.delete.useMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: {
      workspaceName: "",
    },
    resolver: zodResolver(
      z.object({
        workspaceName: z
          .string()
          .min(1, "Please type the name of the workspace.")
          .refine((value) => value === data?.workspace.name, {
            message: "The name of the workspace is incorrect.",
          }),
      }),
    ),
  });

  const onSubmit = async ({ workspaceName }: FormValues) => {
    if (data?.workspace.name === workspaceName) {
      const result = await deleteWorkspace(data.workspace.id);
      void userUtils.get.invalidate();
      void workspaceUtils.getUserWorkspacesList.invalidate();
      void projectUtils.listGroupedByWorkspaceForUser.invalidate();

      toast({ title: "Workspace deleted" });
      if (data?.workspace) {
        logEvent("workspace.delete", {
          workspaceId: data?.workspace.id,
          workspaceName: data?.workspace.name,
        });
      }
      onOpenChange(false);
      if (result.defaultProjectId) {
        navigate(
          generatePath(routes.shops.site, {
            projectId: result.defaultProjectId,
          }),
        );
      } else if (result.defaultWorkspaceId) {
        navigate(
          generatePath(routes.workspace.settings.general, {
            workspaceId: result.defaultWorkspaceId,
          }),
        );
      } else {
        navigate(routes.home.root);
      }
    }
  };

  const canDelete = canDeleteData?.canDelete ?? false;
  const issues = canDeleteData?.issues ?? [];

  const modalContent = (() => {
    if (isCheckingCanDelete) {
      return (
        <div className="mx-auto w-16 py-4">
          <Loader />
        </div>
      );
    }

    if (!canDelete && issues.length > 0) {
      return (
        <div className="flex flex-col gap-2">
          {issues.map((issue) => {
            const navInfo = workspaceId
              ? getIssueNavigationInfo(issue.type, workspaceId)
              : null;
            return (
              <div
                key={issue.type}
                className="border-0.5 border-border rounded-lg"
              >
                <Field orientation="horizontal" className="items-start p-3">
                  <FieldContent>
                    <FieldTitle>
                      {exhaustiveSwitch(issue)({
                        subscription: () => "Active Subscription",
                        projects: () => "Active Shops",
                        experiments: () => "Active A/B Tests",
                      })}
                    </FieldTitle>
                    <FieldDescription>{issue.message}</FieldDescription>
                  </FieldContent>
                  {navInfo && (
                    <div className="flex justify-end">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => navigate(navInfo.path)}
                      >
                        {navInfo.linkText}
                      </Button>
                    </div>
                  )}
                </Field>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <div className="text-muted flex flex-col text-sm">
          <span>Type "{data?.workspace.name}" to confirm deletion.</span>
        </div>
        <LabeledControl
          label="Workspace Name"
          id="workspaceName"
          error={errors.workspaceName?.message}
        >
          <Input
            id="workspaceName"
            autoComplete="off"
            placeholder={data?.workspace.name}
            {...register("workspaceName")}
            type="text"
            size="base"
            invalid={Boolean(errors.workspaceName)}
            autoFocus
          />
        </LabeledControl>
      </div>
    );
  })();

  const description = !canDelete
    ? `${issues.length === 1 ? "One issue" : `${issues.length} issues`} prevent${issues.length > 1 ? "" : "s"} this workspace from being deleted:`
    : "When you delete a workspace, all content such as referral codes and workspace members will also be deleted.";

  const canSubmit = canDelete && isValid;

  const footer = (
    <div className="flex flex-row gap-2 w-full justify-end">
      <Button
        variant="secondary"
        size="base"
        onClick={() => onOpenChange(false)}
      >
        Cancel
      </Button>
      <Button
        variant="destructive"
        size="base"
        render={<button type="submit" form="delete-workspace-form" />}
        disabled={!canSubmit}
        loading={isDeleting}
      >
        Delete
      </Button>
    </div>
  );

  const formContent = (
    <form
      id="delete-workspace-form"
      onSubmit={(formData) => {
        void handleSubmit(onSubmit)(formData);
      }}
    >
      {modalContent}
    </form>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title="Delete Workspace"
        description={description}
        footer={footer}
      >
        {formContent}
      </Drawer>
    );
  }

  return (
    <Modal
      title="Delete Workspace"
      open={open}
      onOpenChange={onOpenChange}
      description={description}
      size="base"
      footer={footer}
    >
      {formContent}
    </Modal>
  );
};
